import View from './view.js'
import React, { useEffect, useState } from 'react'
import useStableValue from 'Logic/useStableValue.js'
import useGoogleMapsPlacesAutocomplete from './useGoogleMapsPlacesAutoComplete.js'

export default function Logic(props) {
  let [isShowingSuggestions, setIsShowingSuggestions] = useState(false)
  let [selected, setSelected] = useState(null)
  // we use a debounced value to query for addresses so we don't hammer the service
  let stableAddress = useStableValue(props.value, 500)

  let autocomplete = useGoogleMapsPlacesAutocomplete({
    address: stableAddress,
    key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    autocompleteOptions: {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    },
  })

  // autocomplete.results stores the previous stableAddress' value when stableAddress === ''
  if (!stableAddress) autocomplete.results = []
  // update the top selected value when the list changes
  useEffect(() => {
    if (!autocomplete.isReady) return
    setSelected(
      autocomplete.results.length > 0 ? autocomplete.results[0].place_id : null
    )
  }, [autocomplete.results, autocomplete.isReady])

  let chooseAddress = async placeId => {
    if (typeof props.onChooseAddress === 'function') {
      // get the actual address because the one before doesn't have all the components
      let place = await autocomplete.getAddressForPlaceId(placeId)
      props.onChooseAddress(mapPlaceToAddress(place), props.id)
    }
    // setTimeout is necessary so that onSubmit is null on first enter in
    // input component
    setTimeout(() => {
      setIsShowingSuggestions(false)
    }, 100)
  }

  let chooseSelectedAddress = () => selected && chooseAddress(selected)

  let onChange = value => {
    if (typeof props.onChange === 'function') {
      props.onChange(value)
    }
    setIsShowingSuggestions(!!value)
  }

  // keyboard navigation up and down
  let prevSuggestion = () => {
    let index = autocomplete.results.findIndex(
      item => item.place_id === selected
    )
    if (index === -1) return

    let prevIndex = index === 0 ? autocomplete.results.length - 1 : index - 1
    setSelected(autocomplete.results[prevIndex].place_id)
  }

  let nextSuggestion = () => {
    let index = autocomplete.results.findIndex(
      item => item.place_id === selected
    )
    if (index === -1) return

    let nextIndex = (index + 1) % autocomplete.results.length
    setSelected(autocomplete.results[nextIndex].place_id)
  }

  let closeSuggestions = () => {
    setTimeout(() => {
      setIsShowingSuggestions(false)
    }, 250)
  }

  // It appears that Chrome now ignores autocomplete="off" unless it is on th
  // <form autocomplete="off"> tag.
  // https://stackoverflow.com/a/16130452/1562732
  return (
    <form autoComplete="off" onSubmit={event => event.preventDefault()}>
      <View
        {...props}
        closeSuggestions={closeSuggestions}
        chooseAddress={chooseAddress}
        chooseSelectedAddress={chooseSelectedAddress}
        from={autocomplete.results}
        isShowingSuggestions={isShowingSuggestions}
        nextSuggestion={nextSuggestion}
        prevSuggestion={prevSuggestion}
        onBlur={closeSuggestions}
        onChange={onChange}
        onSubmit={isShowingSuggestions ? null : props.onSubmit}
        selected={selected}
        value={props.value}
      />
    </form>
  )
}
Logic.defaultProps = {
  value: '',
  id: '',
}

function findComponentInPlace(place, componentType, field = 'long_name') {
  let component = place.address_components?.find(ac =>
    ac.types.includes(componentType)
  )

  if (!component) return ''
  return component[field]
}

function mapPlaceToAddress(place) {
  return {
    street: `${findComponentInPlace(
      place,
      'street_number'
    )} ${findComponentInPlace(place, 'route')}`.trim(),
    city:
      findComponentInPlace(place, 'locality') ||
      findComponentInPlace(place, 'sublocality') ||
      // Brooklyn and other parts of New York City do not include the city as part of the address. Instead, they use sublocality_level_1
      findComponentInPlace(place, 'sublocality_level_1'),
    state: findComponentInPlace(
      place,
      'administrative_area_level_1',
      'short_name'
    ),
    zip: findComponentInPlace(place, 'postal_code'),
  }
}
